import * as utils from '../../core/utils';
import { createIntervalDescription } from './helpers';

export const TaskType = {
  Maintenance: 0,
  Project: 1,
  OpenChecklist: 2,
  ClosingChecklist: 3,
  PreventativeMaintenance: 4,
  CleaningList: 5,
  HourlyInspection: 6,
  ExpressInterior: 7,
} as const;

export type TaskTypeValue = (typeof TaskType)[keyof typeof TaskType];

export const Interval = {
  CarCount: 0,
  Once: 1,
  Daily: 2,
  Weekly: 3,
  Monthly: 4,
  Yearly: 5,
  Hourly: 6,
} as const;

export type IntervalValue = (typeof Interval)[keyof typeof Interval];

export const TaskStatus = {
  Pending: 0,
  Snoozed: 1,
  Completed: 2,
} as const;

export enum ProjectStatus {
  New = 'New',
  OnHold = 'On Hold',
  InProgress = 'In Progress',
  Completed = 'Completed',
}

export const projectStatusOptions = Object.values(ProjectStatus);

export type TaskStatusValue = (typeof TaskStatus)[keyof typeof TaskStatus];

export const dependencies = [
  { id: 0, value: 'DoneBefore', name: 'Done before' },
  { id: 1, value: 'DoneAfter', name: 'Done after' },
  { id: 2, value: 'BlockedBy', name: 'Blocked by' },
  { id: 3, value: 'RelatesTo', name: 'Relates to' },
  { id: 4, value: 'SubTask', name: 'Sub task' },
];

export const forecastType: Record<string, any> = {};

type FormatMessageFunction = (descriptor: { id: string }, values?: Record<string, any>) => string;

export interface PendingTaskProps {
  id?: any;
  name?: string;
  description?: string;
  siteId?: number;
  createdAt?: string;
  creator?: string;
  modifiedAt?: string;
  modifier?: string;
}

/** Shape of one forecast row from the API (for UI logic). */
export type ForecastType = {
  siteId: number;
  taskId: number;
  carsUntilNextCount: number;
  nextScheduledCarCount: number;
  dlyCarAvg: number;
  totalCarCount: number;
  forecastDate: string;
  forecastCarCount: number;
};

export class PendingTask {
  id?: any;
  name?: string;
  description?: string;
  siteId?: number;
  createdAt?: string;
  creator?: string;
  modifiedAt?: string;
  modifier?: string;

  constructor(props: PendingTaskProps = {}) {
    this.id = props.id;
    this.name = props.name;
    this.description = props.description;
    this.siteId = props.siteId;
    this.createdAt = props.createdAt;
    this.creator = props.creator;
    this.modifiedAt = props.modifiedAt;
    this.modifier = props.modifier;
  }
}

export interface TaskProps {
  id?: any;
  name?: string;
  type?: string | number;
  interval?: string | number;
  createdAt?: string;
  creator?: string;
  modifier?: string;
  modifiedAt?: string;
  start?: string;
  end?: string;
  intervalCount?: number;
  weeklySchedule?: string;
  carCount?: number;
  hourlySchedule?: number[];
  monthlySameWeekday?: boolean;
  alertIfOverdue?: boolean;
  siteId?: number;
  latestCompletion?: TaskCompletion;
  description?: string;
  priority?: number | string;
  employeeId?: any;
  videos?: any;
  inventory?: any;
  intervalCarCount?: number;
  startingCarCount?: number;
  groupId?: any;
  prefixId?: number;
  resource?: string;
  locationId?: number;
  operationTypeId?: number;
  modelYears?: any;
  notes?: string;
  linkedTasks?: any;
  linkedCompletions?: any;
  subTitle?: string;
  forecast?: any;
  capacity?: any;
  estimatedTime?: any;
  files?: any;
}

/**
 * Domain model mirroring API payloads. Several fields stay loosely typed because
 * callers compare against strings, spread plain objects into instances, or treat
 * nested data as dynamic — matching the previous implicit-any JavaScript usage.
 */
export class Task {
  id: any;
  name?: string;
  type: any;
  interval: any;
  createdAt?: string;
  creator?: string;
  modifier?: string;
  modifiedAt?: string;
  start?: string;
  end?: string;
  intervalCount: number;
  weeklySchedule?: string;
  carCount?: number;
  hourlySchedule?: number[];
  monthlySameWeekday?: boolean;
  alertIfOverdue?: boolean;
  siteId?: number;
  latestCompletion?: TaskCompletion;
  description?: string;
  priority?: number | string;
  employeeId?: any;
  videos?: any;
  inventory?: any;
  intervalCarCount?: number;
  startingCarCount?: number;
  groupId?: any;
  prefixId?: number;
  resource?: string;
  locationId?: number;
  operationTypeId?: number;
  modelYears?: any;
  notes?: string;
  linkedTasks?: any;
  linkedCompletions?: any;
  subTitle?: string;
  forecast?: ForecastType[];
  capacity?: any;
  estimatedTime?: any;
  files?: any;

  constructor(props: TaskProps = {}) {
    this.id = props.id;
    this.name = props.name;
    this.type = utils.toEnum(TaskType, props.type) as Task['type'];
    this.interval = utils.toEnum(Interval, props.interval) as Task['interval'];
    this.createdAt = props.createdAt;
    this.creator = props.creator;
    this.modifier = props.modifier;
    this.modifiedAt = props.modifiedAt;
    this.start = props.start;
    this.end = props.end;
    this.intervalCount = props.intervalCount ?? 1;
    this.weeklySchedule = props.weeklySchedule;
    this.carCount = props.carCount;
    this.hourlySchedule = props.hourlySchedule;
    this.monthlySameWeekday = props.monthlySameWeekday;
    this.alertIfOverdue = props.alertIfOverdue;
    this.siteId = props.siteId;
    this.latestCompletion = props.latestCompletion;
    this.description = props.description;
    this.priority = props.priority;
    this.employeeId = props.employeeId;
    this.videos = props.videos;
    this.inventory = props.inventory;
    this.intervalCarCount = props.intervalCarCount;
    this.startingCarCount = props.startingCarCount;
    this.groupId = props.groupId;
    this.prefixId = props.prefixId;
    this.resource = props.resource;
    this.locationId = props.locationId;
    this.operationTypeId = props.operationTypeId;
    this.modelYears = props.modelYears;
    this.notes = props.notes;
    this.linkedTasks = props.linkedTasks;
    this.linkedCompletions = props.linkedCompletions;
    this.subTitle = props.subTitle;
    this.forecast = props.forecast;
    this.capacity = props.capacity;
    this.estimatedTime = props.estimatedTime;
    this.files = props.files;
  }

  getIntervalDescription(formatMessage: FormatMessageFunction): string {
    return createIntervalDescription(this, formatMessage);
  }

  get isMasterTask(): boolean {
    return this.groupId !== undefined && this.groupId !== null;
  }
}

export interface TaskCompletionProps {
  id?: number;
  archivedAt?: string;
  createdAt?: string;
  creator?: string;
  employeeId?: any;
  employee?: any;
  modifiedAt?: string;
  modifier?: string;
  taskId?: number;
  timestamp?: string;
  status?: TaskStatusValue | string;
  scheduleEnd?: string;
  scheduleStart?: string;
  scheduleCarCount?: number;
  siteId?: number;
  task?: any;
  carCount?: number;
  notes?: string;
}

export class TaskCompletion {
  id?: number;
  archivedAt?: string;
  createdAt?: string;
  creator?: string;
  employeeId?: any;
  employee?: any;
  modifiedAt?: string;
  modifier?: string;
  taskId?: number;
  timestamp?: string;
  status?: TaskStatusValue | string;
  scheduleEnd?: string;
  scheduleStart?: string;
  scheduleCarCount?: number;
  siteId?: number;
  task?: any;
  carCount?: number;
  notes?: string;

  constructor(props: TaskCompletionProps = {}) {
    this.id = props.id;
    this.archivedAt = props.archivedAt;
    this.createdAt = props.createdAt;
    this.creator = props.creator;
    this.employeeId = props.employeeId;
    this.employee = props.employee;
    this.modifiedAt = props.modifiedAt;
    this.modifier = props.modifier;
    this.taskId = props.taskId;
    this.timestamp = props.timestamp;
    this.status = props.status;
    this.scheduleEnd = props.scheduleEnd;
    this.scheduleStart = props.scheduleStart;
    this.scheduleCarCount = props.scheduleCarCount;
    this.siteId = props.siteId;
    this.task = props.task;
    this.carCount = props.carCount;
    this.notes = props.notes;
  }
}

export const ProjectType = {
  Maintenance: 0,
  Preventative: 1,
  Enhancement: 2,
} as const;

export type ProjectTypeValue = (typeof ProjectType)[keyof typeof ProjectType];

export interface TaskPrefixProps {
  id?: number;
  name?: string;
}

export class TaskPrefix {
  type: any;
  message: any;

  constructor(props: TaskPrefixProps = {}) {
    this.type = props.id;
    this.message = props.name;
  }
}

export interface TaskLocationProps {
  id?: number;
  name?: string;
  orderNumber?: number;
}

export class TaskLocation {
  type: any;
  message: any;
  order: any;

  constructor(props: TaskLocationProps = {}) {
    this.type = props.id;
    this.message = props.name;
    this.order = props.orderNumber;
  }
}
